/**
 * Root mount for the CRM inside the dashboard.
 *
 * Owns the bridge from the dashboard session → CRM session:
 *
 *   1. The user has already authenticated to the dashboard.
 *   2. When they enter /crm/* we synchronously check for a usable
 *      brain crm-api token in localStorage (xop_crm_token + xop_crm_user
 *      + xop_crm_tenant + xop_crm_permissions).
 *   3. If none exist, we GET /api/crm/session from the dashboard server,
 *      which mints a short-TTL crm-api token for the signed-in user (see
 *      `bootstrapCrmSession` below). No CRM password reaches the browser.
 *   4. We dispatch `setAuthData` directly so the CRM Redux store is
 *      already authenticated before any page mounts. The CRM's own
 *      AuthGuard is no longer used (removed from
 *      `(authenticated)/layout.tsx`).
 *   5. Then we render the (authenticated) layout + child route via
 *      <Outlet />.
 */
import { useEffect, useState } from "react";
import { Provider, useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import { Alert, AlertTitle } from "@mui/material";

import AuthenticatedLayout from "@crm-src/pages/CrmLayout";
import { AdminSecurityInitializer } from "@crm-src/components/admin-security-initializer";
import { PageLoader } from "@/components/ui/page-loader";
import { store, type AppDispatch } from "@crm-src/lib/store";
import { setAuthData } from "@crm-src/lib/store/authSlice";
import { tokenStore } from "../lib/tokenStore.js";
import { crmApiUrl } from "@crm-src/lib/api";

interface BootstrapAuthResponse {
  token: string;
  user: { id: number; email: string; full_name?: string; two_factor_enabled?: boolean; is_super_admin?: boolean; [k: string]: unknown };
  tenant: { id: number; name: string; slug: string; [k: string]: unknown };
  permissions?: string[];
}

// The CRM session is minted by the dashboard SERVER (/api/crm/session) —
// NO crm password in the browser. The server signs a short-TTL EdDSA
// crm-api token as the crm operator user and returns
// {token,user,tenant,permissions}. We send the dashboard access token
// (+ cookie) so the server's requireAuth + crm:view gate passes.
async function bootstrapCrmSession(): Promise<BootstrapAuthResponse | null> {
  try {
    const access = tokenStore.getAccess();
    const res = await fetch(crmApiUrl("/api/crm/session"), {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
      credentials: "include",
    });
    if (!res.ok) {
      console.warn("CRM session bootstrap failed", res.status);
      return null;
    }
    return (await res.json()) as BootstrapAuthResponse;
  } catch (err) {
    console.warn("CRM session bootstrap threw", err);
    return null;
  }
}

function persistSession(session: BootstrapAuthResponse): void {
  localStorage.setItem("xop_crm_token", session.token);
  localStorage.setItem("xop_crm_user", JSON.stringify(session.user));
  localStorage.setItem("xop_crm_tenant", JSON.stringify(session.tenant));
  localStorage.setItem("xop_crm_permissions", JSON.stringify(session.permissions ?? []));
}

/**
 * Wraps any subtree that needs a live CRM session. Loads (or restores)
 * the brain crm-api token and dispatches it into the CRM Redux store
 * before rendering children.
 *
 * Exported so the dashboard's global system-admin shell
 * (`apps/web/src/pages/admin/SystemAdminLayout.tsx`) can mount the
 * lifted CRM admin pages outside the /crm/* tree without duplicating
 * the bootstrap logic.
 */
export function CrmSessionBootstrap({ children }: { children: React.ReactNode }): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const [phase, setPhase] = useState<"checking" | "ready" | "failed">("checking");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Always mint a FRESH CRM session on entry — never reuse a cached
      // one. The brain crm-api token is short-TTL, so a cached copy is
      // usually already expired (→ data calls 401 and bounce the user),
      // and a cached user/permissions snapshot can be stale relative to
      // the server: e.g. the CRM operator account the dashboard acts as
      // had its `active` flag flipped server-side (the A2 default-cred
      // cleanup), which the page-permission-guard reads as "inactive" and
      // denies EVERY page. A fresh bootstrap is one fast call and keeps
      // the store + the persisted data-client token in lockstep with
      // current server state.
      const session = await bootstrapCrmSession();
      if (cancelled) return;
      if (!session) {
        setError(
          "Could not bootstrap a CRM session. Check that brain's crm-api is " +
            "reachable at /api/v1/* and that the bootstrap credentials are valid.",
        );
        setPhase("failed");
        return;
      }
      persistSession(session);
      dispatch(
        setAuthData({
          user: session.user,
          tenant: session.tenant,
          permissions: session.permissions ?? [],
        }),
      );
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (phase === "checking") {
    return <PageLoader />;
  }
  if (phase === "failed") {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        <AlertTitle>CRM session unavailable</AlertTitle>
        {error}
      </Alert>
    );
  }
  return <>{children}</>;
}

export function CrmRoot(): JSX.Element {
  return (
    <Provider store={store}>
      <AdminSecurityInitializer />
      <CrmSessionBootstrap>
        <AuthenticatedLayout>
          <Outlet />
        </AuthenticatedLayout>
        {/* No <Toaster> here (removed 2026-09-12). The CRM always renders
            inside AppShell, which mounts the ONE toaster over the ONE shared
            toast store — a second one here painted every CRM toast twice,
            stacked, which is how the reseller-applications e2e caught it
            (the top copy "covered" the bottom one). */}
      </CrmSessionBootstrap>
    </Provider>
  );
}
